<template>
  <v-row>
    <v-col>
      <v-card flat tile elevation="0">
        <v-alert v-if="isCnpjInactive" type="error" dense class="mx-4 mt-4">
          <strong>Edição bloqueada:</strong> CNPJ em situação irregular na Receita Federal. Não é possível realizar alterações.
        </v-alert>
        
        <v-alert v-if="!loading && hasErrors" type="warning" dense class="mx-4 mt-4">
          <div class="d-flex align-center">
            <v-icon color="warning" class="mr-2">mdi-alert-circle-outline</v-icon>
            <div>
              <strong>Dados bancários incompletos.</strong> Os seguintes campos obrigatórios não estão preenchidos:
              <ul class="ma-0 mt-1 pl-4">
                <li v-for="field in missingBankFields" :key="field">{{ field }}</li>
              </ul>
            </div>
          </div>
        </v-alert>
        
        <v-toolbar flat>
          <v-toolbar-title class="d-flex align-center">
            <h6 class="text-h6 font-weight-bold mb-0 mr-4">Financeiro</h6>
            <p class="text-caption black--text ma-0" style="max-width: 700px;">
              Efetuamos pagamentos apenas para contas sob titularidade do veículo de comunicação ou de seus representantes legais. <br> Não são aceitas contas Pessoa Física ou Jurídica sem vínculo com a empresa.
            </p>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-tooltip bottom :disabled="!isCnpjInactive" v-if="!isEditMode">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-btn :disabled="loading || isCnpjInactive" color="primary" @click="enableEditMode">
                  <v-icon left>mdi-pencil</v-icon>
                  Editar dados
                  <v-progress-circular v-if="loading" class="ml-2" color="white" size="20" height="7"
                    indeterminate></v-progress-circular>
                </v-btn>
              </span>
            </template>
            <span>CNPJ inativo. Não é possível realizar alterações</span>
          </v-tooltip>
          <template v-else>
            <v-btn :disabled="loading || isCnpjInactive" color="red" outlined @click="confirmCancel">
              Cancelar
            </v-btn>
            <v-btn :disabled=" !hasChanges || loading || isCnpjInactive" color="success" class="ml-2" @click="confirmSave">
              Salvar
              <v-progress-circular v-if="loading" class="ml-2" color="white" size="20" height="7"
                indeterminate></v-progress-circular>
            </v-btn>
          </template>
        </v-toolbar>
        <div class="v-divider mb-0 grey--text lighten-2"></div>

        <div class="pa-4">
          <v-row>
            <v-col cols="3">
              <label for="document" class="font-weight-black">
                CNPJ do Titular
              </label>

              <v-text-field :disabled="!isEditMode" v-model="form.bankAccount.documentNumber" :readonly="!isEditMode"
                :filled="!isEditMode" :outlined="isEditMode" v-mask="'##.###.###/####-##'" dense placeholder="000.000.000/0000-00"
                :error="hasErrors && (!form.bankAccount.documentNumber || form.bankAccount.documentNumber.trim() === '')"
                :error-messages="hasErrors && (!form.bankAccount.documentNumber || form.bankAccount.documentNumber.trim() === '') ? ['Campo obrigatório não preenchido'] : []">
              </v-text-field>
            </v-col>

            <v-col cols="5">
              <label for="name" class="font-weight-black">
                Nome do Titular
              </label>

              <v-text-field :disabled="!isEditMode" v-model="form.bankAccount.ownerName" :outlined="isEditMode" dense
                :filled="!isEditMode" :readonly="!isEditMode" placeholder="Nome do Titular"
                :error="hasErrors && (!form.bankAccount.ownerName || form.bankAccount.ownerName.trim() === '')"
                :error-messages="hasErrors && (!form.bankAccount.ownerName || form.bankAccount.ownerName.trim() === '') ? ['Campo obrigatório não preenchido'] : []"></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <label for="typeAccount" class="font-weight-black">
                Tipo de Conta
              </label>

              <v-select :disabled="!isEditMode" v-model="form.bankAccount.accountType" :items="accType" item-value="id"
                :filled="!isEditMode" :readonly="!isEditMode" item-text="text" :outlined="isEditMode" dense placeholder="Tipo de Conta"
                :error="hasErrors && !form.bankAccount.accountType"
                :error-messages="hasErrors && !form.bankAccount.accountType ? ['Campo obrigatório não preenchido'] : []"></v-select>
            </v-col>
            <v-col cols="4">
              <label for="typeAccount" class="font-weight-black">
                Natureza da Conta
              </label>

              <v-select :disabled="!isEditMode" v-model="form.bankAccount.type" :items="typeAccountList" :filled="!isEditMode"
                :readonly="!isEditMode" item-value="id" item-text="text" :outlined="isEditMode" dense
                placeholder="Natureza da  Conta"
                :error="hasErrors && !form.bankAccount.type"
                :error-messages="hasErrors && !form.bankAccount.type ? ['Campo obrigatório não preenchido'] : []"></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <label for="bankCode" class="font-weight-black">
                Cód. do Banco
              </label>

              <v-autocomplete :disabled="!isEditMode" v-model="form.bankAccount.bankId" :items="banks"
                :filled="!isEditMode" :readonly="!isEditMode" item-text="nameAndBankCode" item-value="id" :outlined="isEditMode" dense
                placeholder="Cód. do Banco"
                :error="hasErrors && !form.bankAccount.bankId"
                :error-messages="hasErrors && !form.bankAccount.bankId ? ['Campo obrigatório não preenchido'] : []"></v-autocomplete>
            </v-col>
            <v-col cols="1">
              <label for="agency" class="font-weight-black"> Agência </label>
              <v-text-field :disabled="!isEditMode" v-model="form.bankAccount.agencyNumber" :outlined="isEditMode" dense
                v-mask="'######'" :filled="!isEditMode" :readonly="!isEditMode" placeholder="Agência"
                :error="hasErrors && (!form.bankAccount.agencyNumber || form.bankAccount.agencyNumber.trim() === '')"
                :error-messages="hasErrors && (!form.bankAccount.agencyNumber || form.bankAccount.agencyNumber.trim() === '') ? ['Campo obrigatório não preenchido'] : []"></v-text-field>
            </v-col>

            <v-col cols="3">
              <label for="account" class="font-weight-black"> Conta </label>

              <v-text-field :disabled="!isEditMode" v-model="form.bankAccount.accountNumber" :outlined="isEditMode" dense
                :filled="!isEditMode" :readonly="!isEditMode" placeholder="Conta"
                :error="hasErrors && (!form.bankAccount.accountNumber || form.bankAccount.accountNumber.trim() === '')"
                :error-messages="hasErrors && (!form.bankAccount.accountNumber || form.bankAccount.accountNumber.trim() === '') ? ['Campo obrigatório não preenchido'] : []"></v-text-field>
            </v-col>
          </v-row>
          <v-row>

            <v-col cols="8">
              <label for="pix" class="font-weight-black"> Chave PIX </label>

              <v-text-field :disabled="!isEditMode" v-model="form.bankAccount.pix" :outlined="isEditMode" dense hide-details :readonly="!isEditMode"
                :filled="!isEditMode" placeholder="Chave PIX"></v-text-field>
            </v-col>

          </v-row>
          <v-row>

            <v-col cols="4">
              <label for="cellphone" class="font-weight-black">
                Nome do contato
              </label>
              <v-text-field :disabled="!isEditMode" v-model="form.bankAccount.managerName" :outlined="isEditMode" dense
                :filled="!isEditMode" :readonly="!isEditMode" placeholder="Nome do contato"
                :error="hasErrors && (!form.bankAccount.managerName || form.bankAccount.managerName.trim() === '')"
                :error-messages="hasErrors && (!form.bankAccount.managerName || form.bankAccount.managerName.trim() === '') ? ['Campo obrigatório não preenchido'] : []"></v-text-field>
            </v-col>

            <v-col cols="2">
              <label for="cellphone" class="font-weight-black">
                Telefone 1
              </label>
              <v-text-field :disabled="!isEditMode" v-model="form.bankAccount.managerContact" :outlined="isEditMode"
                :readonly="!isEditMode" :filled="!isEditMode" v-mask="'(##) #####-####'" dense placeholder="Telefone 1"
                :error="hasErrors && (!form.bankAccount.managerContact || form.bankAccount.managerContact.trim() === '')"
                :error-messages="hasErrors && (!form.bankAccount.managerContact || form.bankAccount.managerContact.trim() === '') ? ['Campo obrigatório não preenchido'] : []"></v-text-field>
            </v-col>
            <v-col cols="2">
              <label for="cellphone2" class="font-weight-black">
                Telefone 2
              </label>
              <v-text-field :disabled="!isEditMode" v-model="form.bankAccount.managerPhone" :outlined="isEditMode" hide-details
                :filled="!isEditMode" :readonly="!isEditMode" v-mask="'(##) #####-####'" dense placeholder="Telefone 2"></v-text-field>
            </v-col>
          </v-row>

          <v-snackbar v-model="showSnackbarSuccess" :timeout="3000" color="success" top>
            {{ messageSuccessSnackbar }}
          </v-snackbar>

          <v-snackbar v-model="showSnackbarError" :timeout="3000" color="red" top>
            {{ messageErrorSnackbar }}
          </v-snackbar>
        </div>

        <v-dialog v-model="cancelDialog" max-width="400">
          <v-card>
            <v-card-title class="text-h6">
              Confirmação
            </v-card-title>
            <v-card-text>
              Tem certeza que deseja cancelar? Todas as alterações não salvas serão perdidas.
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="grey" text @click="cancelDialog = false">
                Não
              </v-btn>
              <v-btn color="primary" @click="cancelEdit">
                Sim, cancelar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="saveDialog" max-width="500">
          <v-card>
            <v-card-title class="text-h6">
              Confirmar dados bancários
            </v-card-title>
            <v-card-text>
              <p class="mb-2">Você está prestes a salvar os seguintes dados bancários que serão enviados ao financeiro.</p>
              <p class="mt-4 font-weight-bold red--text">Confirme se todos os dados estão corretos antes de salvar.</p>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="grey" text @click="saveDialog = false">
                Cancelar
              </v-btn>
              <v-btn color="success" @click="confirmSaveAction">
                Confirmar e salvar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import axios from "axios";

export default {
  name: "BankDataProfile",

  props: {
    profileId: {
      type: String,
      required: true,
    },
    profile: {
      type: Object,
      default: null,
    },
    isCnpjInactive: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    formIsValid() {
      return (
        this.form.bankAccount.bankId &&
        this.form.bankAccount.accountType &&
        this.form.bankAccount.agencyNumber &&
        this.form.bankAccount.accountNumber &&
        this.form.bankAccount.type &&
        this.form.bankAccount.ownerName &&
        this.form.bankAccount.documentNumber
      )
    },
    hasChanges() {
      return JSON.stringify(this.form.bankAccount) !== JSON.stringify(this.originalData)
    },
    missingBankFields() {
      const missing = [];
      if (!this.form.bankAccount.documentNumber || String(this.form.bankAccount.documentNumber).trim() === '') missing.push('CNPJ do Titular');
      if (!this.form.bankAccount.ownerName || String(this.form.bankAccount.ownerName).trim() === '') missing.push('Nome do Titular');
      if (!this.form.bankAccount.accountType) missing.push('Tipo de Conta');
      if (!this.form.bankAccount.type) missing.push('Natureza da Conta');
      if (!this.form.bankAccount.bankId) missing.push('Código do Banco');
      if (!this.form.bankAccount.agencyNumber || String(this.form.bankAccount.agencyNumber).trim() === '') missing.push('Agência');
      if (!this.form.bankAccount.accountNumber || String(this.form.bankAccount.accountNumber).trim() === '') missing.push('Conta');
      if (!this.form.bankAccount.managerName || String(this.form.bankAccount.managerName).trim() === '') missing.push('Nome do contato');
      if (!this.form.bankAccount.managerContact || String(this.form.bankAccount.managerContact).trim() === '') missing.push('Telefone 1');
      return missing;
    },
    hasErrors() {
      return this.missingBankFields.length > 0;
    }
  },

  data() {
    return {
      banks: [],
      originalData: {},
      showSnackbarSuccess: false,
      messageSuccessSnackbar: "",
      showSnackbarError: false,
      messageErrorSnackbar: "",
      isEditMode: false,
      cancelDialog: false,
      saveDialog: false,
      accType: [
        {
          id: 1, text: "Conta corrente"
        },
        {
          id: 2, text: "Conta poupança"
        }
      ],
      typeAccountList: [
        {
          id: 2,
          text: "Júridica",
        },
      ],
      loading: false,
      form: {
        bankAccount: {
          id: null,
          bankId: null,
          accountType: null,
          agencyNumber: "",
          accountNumber: "",
          pix: "",
          type: null,
          managerContact: "",
          managerPhone: "",
          managerName: "",
          ownerName: "",
          documentNumber: "",
          bankCode: null,
        },
      },
    };
  },
  
  created() {
    this.getAllBanks();
    this.getBankFromDB();
  },

  methods: {
    enableEditMode() {
      this.isEditMode = true;
      this.originalData = JSON.parse(JSON.stringify(this.form.bankAccount));
    },

    confirmSave() {
      this.saveDialog = true;
    },

    confirmSaveAction() {
      this.saveDialog = false;
      this.submitBankAccountForm();
    },

    getBankName(bankId) {
      const bank = this.banks.find(b => b.id === bankId);
      return bank ? bank.nameAndBankCode : '';
    },

    confirmCancel() {
      this.cancelDialog = true;
    },

    cancelEdit() {
      this.form.bankAccount = JSON.parse(JSON.stringify(this.originalData));
      this.isEditMode = false;
      this.cancelDialog = false;
    },
    
    async getAllBanks() {
      try {
        const response = await this.$axios.get(`assets/banks`);

        const allBanks = response.data.data;
        const allBanksFormatted = allBanks.map((bank) => ({
          id: bank.bankId,
          bankId: bank.febrabanCode,
          name: bank.name,
          nameAndBankCode: `${bank.febrabanCode} - ${bank.name}`,
        }));

        allBanksFormatted.sort((a, b) => a.bankId - b.bankId);
        this.banks = allBanksFormatted;
      } catch (error) {
        console.error('Erro ao carregar bancos:', error);
      }
    },

    async getBankFromDB() {
      try {
        this.loading = true;
        const response = await this.$axios.get(`bankAccount/profileId/${this.profileId}`);

        const currentBankAccount = response.data.data[0];

        if (currentBankAccount) {
          this.originalData = {
            bankId: parseInt(currentBankAccount.bankId),
            agencyNumber: String(currentBankAccount.agencyNumber || ''),
            accountNumber: String(currentBankAccount.accountNumber || ''),
            pix: currentBankAccount.pix || '',
            type: currentBankAccount.type,
            accountType: parseInt(currentBankAccount.accountType),
            managerContact: currentBankAccount.managerContact || '',
            managerPhone: currentBankAccount.managerPhone || '',
            managerName: currentBankAccount.managerName || '',
            ownerName: currentBankAccount.ownerName || '',
            documentNumber: currentBankAccount.documentNumber || '',
            bankCode: currentBankAccount.bankCode,
            id: currentBankAccount.id,
          };
        }

        this.form.bankAccount = { ...this.originalData };
      } catch (error) {
        console.error('Erro ao carregar dados bancários:', error);
      } finally {
        this.loading = false;
      }
    },

    async submitBankAccountForm() {
      this.loading = true;

      const form = { 
        ...this.form.bankAccount,
        pix: this.form.bankAccount.pix ? `${this.form.bankAccount.pix}` : null, 
        profileId: this.profileId 
      }
      
      if (this.form.bankAccount.id) {
        await this.updateBankAccount(form);
      } else {
        await this.createBankAccount(form);
      }
    },
    
    async createBankAccount(form) {
      try {        
        await this.$axios.post(`bankAccount`, form);

        await this.updateBankDataProfileInOmie(form);
        await this.getBankFromDB();
        
        this.messageSuccessSnackbar = "Dados bancários salvos com sucesso!";
        this.showSnackbarSuccess = true;
        this.isEditMode = false;
      } catch (error) {
        console.error('Erro ao criar dados bancários:', error);
        this.messageErrorSnackbar = "Erro ao salvar dados bancários!";
        this.showSnackbarError = true;
      } finally {
        this.loading = false;
      }
    },
    
    async updateBankAccount(form) {
      try {        
        await this.$axios.put(`bankAccount/${this.form.bankAccount.id}`, form);
        await this.updateBankDataProfileInOmie(form);
        await this.getBankFromDB();
        
        this.messageSuccessSnackbar = "Dados bancários atualizados com sucesso!";
        this.showSnackbarSuccess = true;
        this.isEditMode = false;
      } catch (error) {
        console.error('Erro ao atualizar dados bancários:', error);
        this.messageErrorSnackbar = "Erro ao atualizar dados bancários!";
        this.showSnackbarError = true;
      } finally {
        this.loading = false;
      }
    },
    
    
    async updateBankDataProfileInOmie(form) {
      let dadosBancarios = {
        codigo_banco: '',
        agencia: '',
        conta_corrente: '',
        doc_titular: '',
        nome_titular: '',
        transf_padrao: '',
        cChavePix: ''
      }
      
      if (form) {
        dadosBancarios.agencia = form.agencyNumber
        dadosBancarios.conta_corrente = form.accountNumber
        dadosBancarios.cChavePix = form.pix
        dadosBancarios.doc_titular = form.documentNumber
        dadosBancarios.nome_titular = form.ownerName
        dadosBancarios.transf_padrao = 'S'
        dadosBancarios.codigo_banco = form.bankCode
      } else {
        return null
      }
      
      let data = {
        razao_social: this.profile.name,
        nome_fantasia: this.profile.displayName,
        cnpj_cpf: this.profile.document.replace(/[^\d]/g, ""),
        codigo_cliente_integracao: this.profileId,
        dadosBancarios
      }

      try {
        
        await this.$axios.put(`omie/update-customer/${this.profileId}`, data);
      } catch (error) {
        console.error('Error on update publisher in omie', error)
      }
    },
  }
};
</script>

<style scoped>
* {
  font-family: 'Roboto', sans-serif !important;
}

.text-blue {
  color: #233a75;
}
</style>
